import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { paymentService } from '../../services/paymentService';
import { reconciliationService } from '../../services/reconciliationService';
import { emailReceiptService } from '../../services/emailReceiptService';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import StatsCard from '../admin-dashboard/components/StatsCard';
import TransactionTrendingChart from './components/TransactionTrendingChart';
import RevenueForecastPanel from './components/RevenueForecastPanel';
import PaymentMethodPerformance from './components/PaymentMethodPerformance';
import TransactionMatchingInterface from './components/TransactionMatchingInterface';
import ReconciliationExceptionsPanel from './components/ReconciliationExceptionsPanel';
import EmailReceiptTracker from './components/EmailReceiptTracker';

const PaymentReconciliationDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isSuperAdmin, loading: authLoading } = useAuth();
  const [reconciliationData, setReconciliationData] = useState([]);
  const [stats, setStats] = useState({
    totalRecords: 0,
    matched: 0,
    unmatched: 0,
    disputed: 0,
    totalDiscrepancy: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState('30');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateNotes, setUpdateNotes] = useState('');

  // Analytics state
  const [trendData, setTrendData] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [forecastLoading, setForecastLoading] = useState(false);

  // New state for reconciliation features
  const [exceptions, setExceptions] = useState([]);
  const [emailReceipts, setEmailReceipts] = useState([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(false);
  const [emailReceiptsLoading, setEmailReceiptsLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isSuperAdmin))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isSuperAdmin, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isSuperAdmin)) {
      loadReconciliationData();
      loadAnalyticsData();
      loadExceptions();
      loadEmailReceipts();
    }
  }, [currentTenant, isGymOwner, isSuperAdmin, filterStatus, dateRange]);

  const loadReconciliationData = async () => {
    setLoading(true);
    setError('');
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      const filters = {
        startDate: startDate?.toISOString()?.split('T')?.[0]
      };
      if (filterStatus !== 'all') filters.status = filterStatus;

      const data = await reconciliationService?.getReconciliationData(currentTenant?.id, filters);
      setReconciliationData(data);

      // Calculate stats
      const matched = data?.filter(r => r?.reconciliation_status === 'matched')?.length || 0;
      const unmatched = data?.filter(r => r?.reconciliation_status === 'unmatched')?.length || 0;
      const disputed = data?.filter(r => r?.reconciliation_status === 'disputed')?.length || 0;
      const totalDiscrepancy = data?.reduce((sum, r) => sum + Math.abs(parseFloat(r?.amount_difference || 0)), 0) || 0;

      setStats({
        totalRecords: data?.length || 0,
        matched,
        unmatched,
        disputed,
        totalDiscrepancy
      });
    } catch (err) {
      setError(err?.message || 'Failed to load reconciliation data');
      console.error('Load reconciliation data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalyticsData = async () => {
    setAnalyticsLoading(true);
    try {
      const days = parseInt(dateRange);
      
      const trends = await paymentService?.getTransactionTrends(currentTenant?.id, days);
      setTrendData(trends);

      const performance = await paymentService?.getPaymentMethodPerformance(currentTenant?.id, days);
      setPerformanceData(performance);
    } catch (err) {
      console.error('Load analytics data error:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadExceptions = async () => {
    setExceptionsLoading(true);
    try {
      const data = await reconciliationService?.getReconciliationExceptions(currentTenant?.id, { status: 'open' });
      setExceptions(data);
    } catch (err) {
      console.error('Load exceptions error:', err);
    } finally {
      setExceptionsLoading(false);
    }
  };

  const loadEmailReceipts = async () => {
    setEmailReceiptsLoading(true);
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      const data = await emailReceiptService?.getEmailReceipts(currentTenant?.id, {
        startDate: startDate?.toISOString()?.split('T')?.[0]
      });
      setEmailReceipts(data);
    } catch (err) {
      console.error('Load email receipts error:', err);
    } finally {
      setEmailReceiptsLoading(false);
    }
  };

  const loadRevenueForecast = async () => {
    setForecastLoading(true);
    try {
      const forecastData = await paymentService?.getRevenueForecast(currentTenant?.id);
      setForecast(forecastData);
      setSuccess('Revenue forecast generated successfully');
    } catch (err) {
      setError(err?.message || 'Failed to generate revenue forecast');
      console.error('Load revenue forecast error:', err);
    } finally {
      setForecastLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedRecord || !updateStatus) return;

    try {
      await reconciliationService?.updateReconciliationStatus(
        selectedRecord?.id,
        updateStatus,
        user?.id,
        updateNotes
      );
      setSuccess('Reconciliation status updated successfully');
      setShowUpdateModal(false);
      setSelectedRecord(null);
      setUpdateStatus('');
      setUpdateNotes('');
      loadReconciliationData();
    } catch (err) {
      setError(err?.message || 'Failed to update reconciliation status');
      console.error('Update reconciliation status error:', err);
    }
  };

  const handleMatchTransactions = async (id1, id2) => {
    try {
      await reconciliationService?.matchTransactions(id1, id2, user?.id);
      setSuccess('Transactions matched successfully');
      loadReconciliationData();
    } catch (err) {
      setError(err?.message || 'Failed to match transactions');
      console.error('Match transactions error:', err);
    }
  };

  const handleResolveException = async (exceptionId) => {
    try {
      const notes = prompt('Enter resolution notes:');
      if (notes) {
        await reconciliationService?.resolveException(exceptionId, user?.id, notes);
        setSuccess('Exception resolved successfully');
        loadExceptions();
      }
    } catch (err) {
      setError(err?.message || 'Failed to resolve exception');
      console.error('Resolve exception error:', err);
    }
  };

  const handleRetryEmail = async (receiptId) => {
    try {
      await emailReceiptService?.retryEmailReceipt(receiptId);
      setSuccess('Email resent successfully');
      loadEmailReceipts();
    } catch (err) {
      setError(err?.message || 'Failed to resend email');
      console.error('Retry email error:', err);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      matched: 'bg-success/10 text-success',
      unmatched: 'bg-warning/10 text-warning',
      disputed: 'bg-destructive/10 text-destructive',
      resolved: 'bg-primary/10 text-primary'
    };
    return badges?.[status] || 'bg-muted text-muted-foreground';
  };

  const getStatusIcon = (status) => {
    const icons = {
      matched: 'CheckCircle',
      unmatched: 'AlertCircle',
      disputed: 'XCircle',
      resolved: 'Check'
    };
    return icons?.[status] || 'Circle';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-6 py-8 pb-24">
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/billing-subscription-center')}
            iconName="ArrowLeft"
            iconPosition="left"
            className="mb-4"
          >
            Back to Billing
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Payment Reconciliation</h1>
              <p className="text-muted-foreground">Transaction matching, email receipts, and financial oversight</p>
            </div>
            <Button onClick={loadReconciliationData} variant="outline" iconName="RefreshCw" iconPosition="left">
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
            <p className="text-success text-sm">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <StatsCard
            title="Total Records"
            value={stats?.totalRecords}
            icon="FileText"
            iconColor="var(--color-primary)"
            subtitle=""
            trend="neutral"
            trendValue={0}
          />
          <StatsCard
            title="Matched"
            value={stats?.matched}
            icon="CheckCircle"
            iconColor="var(--color-success)"
            subtitle=""
            trend="neutral"
            trendValue={0}
          />
          <StatsCard
            title="Unmatched"
            value={stats?.unmatched}
            icon="AlertCircle"
            iconColor="var(--color-warning)"
            subtitle=""
            trend="neutral"
            trendValue={0}
          />
          <StatsCard
            title="Disputed"
            value={stats?.disputed}
            icon="XCircle"
            iconColor="var(--color-destructive)"
            subtitle=""
            trend="neutral"
            trendValue={0}
          />
          <StatsCard
            title="Discrepancy"
            value={`$${stats?.totalDiscrepancy?.toFixed(2)}`}
            icon="DollarSign"
            iconColor="var(--color-warning)"
            subtitle=""
            trend="neutral"
            trendValue={0}
          />
        </div>

        {/* Transaction Matching Interface */}
        <div className="mb-8">
          <TransactionMatchingInterface
            transactions={reconciliationData?.filter(r => r?.reconciliation_status === 'unmatched' || r?.reconciliation_status === 'pending_review')}
            onMatch={handleMatchTransactions}
            onUpdateStatus={handleUpdateStatus}
            loading={loading}
          />
        </div>

        {/* Reconciliation Exceptions */}
        <div className="mb-8">
          <ReconciliationExceptionsPanel
            exceptions={exceptions}
            onResolve={handleResolveException}
            loading={exceptionsLoading}
          />
        </div>

        {/* Email Receipt Tracker */}
        <div className="mb-8">
          <EmailReceiptTracker
            receipts={emailReceipts}
            onRetry={handleRetryEmail}
            loading={emailReceiptsLoading}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <TransactionTrendingChart trendData={trendData} loading={analyticsLoading} />
          <PaymentMethodPerformance performanceData={performanceData} loading={analyticsLoading} />
        </div>

        <div className="mb-8">
          <RevenueForecastPanel
            forecast={forecast}
            loading={forecastLoading}
            onRefresh={loadRevenueForecast}
          />
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default PaymentReconciliationDashboard;